/**
 * Session Restore Utility
 * Provides seamless UX while maintaining security with in-memory tokens
 *
 * Security: Only stores session metadata (user_id, expiry) in localStorage,
 * NOT the actual JWT tokens. Tokens remain in-memory only.
 *
 * FIXED: Now uses localStorage instead of sessionStorage to persist across
 * browser tabs and windows (matches Supabase SDK storage strategy)
 */

import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { clearAllAuthStorage } from './storageCleanup';

const RESTORE_HINT_KEY = 'hover_session_hint';

interface SessionHint {
  user_id: string;
  expires_at: string;
  created_at: number;
}

/**
 * True while /auth/callback holds a code (or tokens) to exchange.
 * AuthCallback.tsx owns that exchange; startup session work must stay out of its way.
 */
export function isAuthCallbackInProgress(href: string = window.location.href): boolean {
  try {
    const url = new URL(href);
    if (!url.pathname.includes('/auth/callback')) return false;
    return url.searchParams.has('code') || url.hash.includes('access_token') || url.hash.includes('type=recovery');
  } catch {
    return false;
  }
}

/**
 * Write the session hint from a session object — synchronous, no Supabase call.
 * Safe inside onAuthStateChange, which supabase-js awaits while it holds its auth lock.
 */
export function writeSessionHint(session: Pick<Session, 'expires_at' | 'user'>): void {
  // Supabase expires_at is a Unix timestamp in seconds
  const expiresAtISO = typeof session.expires_at === 'number'
    ? new Date(session.expires_at * 1000).toISOString()
    : new Date(Date.now() + 3600000).toISOString(); // fallback: 1 hour from now

  const hint: SessionHint = {
    user_id: session.user.id,
    expires_at: expiresAtISO,
    created_at: Date.now(),
  };

  localStorage.setItem(RESTORE_HINT_KEY, JSON.stringify(hint));
  console.log('[SessionRestore] ✅ Stored session hint for user:', hint.user_id, 'expires', expiresAtISO);
}

/**
 * Store minimal session hint for restore
 * (NOT the actual tokens, just metadata for UX)
 * Calls getSession() — never from inside onAuthStateChange; use writeSessionHint there.
 */
export async function storeSessionHint(): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session) writeSessionHint(data.session);
  } catch (error) {
    console.error('[SessionRestore] Failed to store hint:', error);
  }
}

/**
 * Attempt to restore session on page load
 * Returns true if session was restored, false otherwise
 */
export async function restoreSession(): Promise<boolean> {
  try {
    const hintJson = localStorage.getItem(RESTORE_HINT_KEY);

    if (!hintJson) {
      console.log('[SessionRestore] No session hint found in localStorage');
      return false;
    }

    const hint: SessionHint = JSON.parse(hintJson);
    console.log('[SessionRestore] Found session hint for user:', hint.user_id);

    // Check if session hint is expired
    const expiresAt = new Date(hint.expires_at);
    const now = new Date();

    if (expiresAt <= now) {
      console.log('[SessionRestore] Session hint expired');
      localStorage.removeItem(RESTORE_HINT_KEY);
      return false;
    }

    // Attempt to refresh the session using refresh token
    // Note: Supabase automatically manages refresh tokens in localStorage
    const { data, error } = await supabase.auth.refreshSession();

    if (error || !data.session) {
      console.log('[SessionRestore] Session refresh failed:', error?.message);

      // If it's a JWT/token error, clean all auth storage
      if (error?.message?.includes('JWT') || error?.message?.includes('token') || error?.message?.includes('refresh')) {
        console.log('[SessionRestore] 🔧 Token error detected, cleaning auth storage...');
        clearAllAuthStorage();
        console.log('[SessionRestore] ✅ Auth storage cleaned');
      } else {
        localStorage.removeItem(RESTORE_HINT_KEY);
      }

      return false;
    }

    // Success! Update the hint with new expiry
    await storeSessionHint();

    console.log('[SessionRestore] ✅ Session restored for user:', hint.user_id);
    return true;
  } catch (error) {
    console.error('[SessionRestore] Error during restore:', error);
    localStorage.removeItem(RESTORE_HINT_KEY);
    return false;
  }
}

/**
 * Initialize auth on app load
 * Tries to restore session, falls back to checking for OAuth callback
 */
export async function initializeAuth(): Promise<boolean> {
  console.log('[SessionRestore] Initializing auth...');

  // On /auth/callback the code exchange owns the session; a refresh here would compete with it
  if (isAuthCallbackInProgress()) {
    console.log('[SessionRestore] ⏭️ Auth callback in progress — skipping restore');
    return false;
  }

  // Try to restore existing session
  const restored = await restoreSession();

  if (restored) {
    return true;
  }

  // Check for new OAuth callback session
  const { data } = await supabase.auth.getSession();

  if (data.session) {
    console.log('[SessionRestore] ✅ New session detected from OAuth');
    await storeSessionHint();
    return true;
  }

  console.log('[SessionRestore] No session found');
  return false;
}

/**
 * Clear session hint on logout
 */
export function clearSessionHint(): void {
  localStorage.removeItem(RESTORE_HINT_KEY);
  console.log('[SessionRestore] Session hint cleared from localStorage');
}

/**
 * Check if we have a session hint (for quick UX check)
 * Note: This doesn't mean the session is valid, just that we might be able to restore it
 */
export function hasSessionHint(): boolean {
  const hint = localStorage.getItem(RESTORE_HINT_KEY);

  if (!hint) {
    return false;
  }

  try {
    const parsed: SessionHint = JSON.parse(hint);
    const expiresAt = new Date(parsed.expires_at);
    return expiresAt > new Date();
  } catch {
    return false;
  }
}
